import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/TodoAppAdvanced.css';

interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string;
  assignee: string;
  tags: string[];
  createdAt: Date;
  completedAt?: Date;
  subtasks: SubTask[];
  timeSpent: number;
  estimatedTime: number;
}

interface SubTask {
  id: string;
  title: string;
  completed: boolean;
}

interface Filter {
  status: 'all' | 'active' | 'completed';
  priority: 'all' | 'low' | 'medium' | 'high' | 'urgent';
  assignee: 'all' | string;
  search: string;
}

interface User {
  id: string;
  name: string;
  avatar: string;
  color: string;
}

const MOCK_USERS: User[] = [
  { id: '1', name: 'Alice Johnson', avatar: '👩‍💼', color: '#3B82F6' },
  { id: '2', name: 'Bob Smith', avatar: '👨‍💻', color: '#10B981' },
  { id: '3', name: 'Carol Davis', avatar: '👩‍🎨', color: '#F59E0B' },
  { id: '4', name: 'David Wilson', avatar: '👨‍🔬', color: '#EF4444' },
];

const TodoAppAdvanced: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filter, setFilter] = useState<Filter>({
    status: 'all',
    priority: 'all',
    assignee: 'all',
    search: ''
  });
  const [draggedTask, setDraggedTask] = useState<string | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [realTimeUsers, setRealTimeUsers] = useState<number>(3);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Simulate real-time collaboration
  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeUsers(prev => prev + Math.floor(Math.random() * 3) - 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load demo tasks
  useEffect(() => {
    const demoTasks: Task[] = [
      {
        id: '1',
        title: 'Implement user authentication',
        description: 'Set up OAuth2 with social login options',
        completed: false,
        priority: 'high',
        dueDate: '2024-01-15',
        assignee: 'Alice Johnson',
        tags: ['backend', 'security'],
        createdAt: new Date(),
        subtasks: [
          { id: '1a', title: 'Setup OAuth providers', completed: true },
          { id: '1b', title: 'Create login UI', completed: false }
        ],
        timeSpent: 180,
        estimatedTime: 480
      },
      {
        id: '2',
        title: 'Design system components',
        description: 'Create reusable UI component library',
        completed: false,
        priority: 'medium',
        dueDate: '2024-01-20',
        assignee: 'Carol Davis',
        tags: ['frontend', 'design'],
        createdAt: new Date(),
        subtasks: [],
        timeSpent: 240,
        estimatedTime: 600
      },
      {
        id: '3',
        title: 'Database optimization',
        description: 'Optimize queries and add indexes',
        completed: true,
        priority: 'urgent',
        dueDate: '2024-01-10',
        assignee: 'Bob Smith',
        tags: ['database', 'performance'],
        createdAt: new Date(),
        completedAt: new Date(),
        subtasks: [],
        timeSpent: 360,
        estimatedTime: 300
      }
    ];
    setTasks(demoTasks);
  }, []);

  const addTask = () => {
    if (!newTaskTitle.trim()) return;

    const newTask: Task = {
      id: Date.now().toString(),
      title: newTaskTitle,
      description: '',
      completed: false,
      priority: 'medium',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      assignee: MOCK_USERS[0].name,
      tags: [],
      createdAt: new Date(),
      subtasks: [],
      timeSpent: 0,
      estimatedTime: 60
    };

    setTasks(prev => [newTask, ...prev]);
    setNewTaskTitle('');
  };

  const toggleTask = (taskId: string) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { 
            ...task, 
            completed: !task.completed,
            completedAt: !task.completed ? new Date() : undefined
          }
        : task
    ));
  };

  const updateTaskPriority = (taskId: string, priority: Task['priority']) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId ? { ...task, priority } : task
    ));
  };

  const filteredTasks = tasks.filter(task => {
    if (filter.status === 'active' && task.completed) return false;
    if (filter.status === 'completed' && !task.completed) return false;
    if (filter.priority !== 'all' && task.priority !== filter.priority) return false;
    if (filter.assignee !== 'all' && task.assignee !== filter.assignee) return false;
    if (filter.search && !task.title.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'low': return '#10B981';
      case 'medium': return '#F59E0B';
      case 'high': return '#EF4444';
      case 'urgent': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getProgressPercentage = (task: Task) => {
    if (task.completed) return 100;
    if (task.subtasks.length === 0) return task.timeSpent / task.estimatedTime * 100;
    const completedSubtasks = task.subtasks.filter(sub => sub.completed).length;
    return (completedSubtasks / task.subtasks.length) * 100;
  };

  return (
    <div className="todo-app-advanced">
      <div className="header-advanced">
        <div className="header-left">
          <Link to="/" className="back-button-advanced">← Back to Home</Link>
          <div className="title-section">
            <h1>🚀 Advanced Task Manager</h1>
            <div className="version-badge-advanced v15">Synoppy 1.5</div>
          </div>
        </div>
        
        <div className="header-right">
          <div className="real-time-indicator">
            <div className="online-users">
              {MOCK_USERS.slice(0, realTimeUsers).map(user => (
                <div key={user.id} className="user-avatar" style={{ backgroundColor: user.color }}>
                  {user.avatar}
                </div>
              ))}
              <div className="live-indicator">🟢 Live</div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-number">{tasks.filter(t => !t.completed).length}</div>
          <div className="stat-label">Active Tasks</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{tasks.filter(t => t.completed).length}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{tasks.filter(t => t.priority === 'urgent').length}</div>
          <div className="stat-label">Urgent</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{Math.round(tasks.reduce((acc, t) => acc + t.timeSpent, 0) / 60)}h</div>
          <div className="stat-label">Time Logged</div>
        </div>
      </div>

      <div className="controls-advanced">
        <div className="add-task-section">
          <div className="quick-add">
            <input
              type="text"
              placeholder="Add a new task..."
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTask()}
              className="quick-add-input"
            />
            <button onClick={addTask} className="add-button">
              <span>+</span> Add Task
            </button>
          </div>
        </div>

        <div className="filters-advanced">
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={filter.status} 
              onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value as Filter['status'] }))}
              className="filter-select"
            >
              <option value="all">All Tasks</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Priority:</label>
            <select 
              value={filter.priority}
              onChange={(e) => setFilter(prev => ({ ...prev, priority: e.target.value as Filter['priority'] }))}
              className="filter-select"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Assignee:</label>
            <select 
              value={filter.assignee}
              onChange={(e) => setFilter(prev => ({ ...prev, assignee: e.target.value }))}
              className="filter-select"
            >
              <option value="all">All Assignees</option>
              {MOCK_USERS.map(user => (
                <option key={user.id} value={user.name}>{user.name}</option>
              ))}
            </select>
          </div>

          <div className="search-group">
            <input
              type="text"
              placeholder="Search tasks..."
              value={filter.search}
              onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
              className="search-input"
            />
          </div>
        </div>
      </div>

      <div className="tasks-container">
        <div className="tasks-list">
          {filteredTasks.map(task => (
            <div 
              key={task.id} 
              className={`task-card-advanced ${task.completed ? 'completed' : ''}`}
              draggable
              onDragStart={() => setDraggedTask(task.id)}
              onDragEnd={() => setDraggedTask(null)}
            >
              <div className="task-header">
                <div className="task-check">
                  <input
                    type="checkbox"
                    checked={task.completed}
                    onChange={() => toggleTask(task.id)}
                    className="checkbox-advanced"
                  />
                </div>
                
                <div className="task-content">
                  <div className="task-title">{task.title}</div>
                  {task.description && (
                    <div className="task-description">{task.description}</div>
                  )}
                  
                  <div className="task-tags">
                    {task.tags.map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>

                  {task.subtasks.length > 0 && (
                    <div className="subtasks">
                      <div className="subtasks-progress">
                        <div 
                          className="progress-bar"
                          style={{ width: `${getProgressPercentage(task)}%` }}
                        ></div>
                      </div>
                      <span className="subtasks-count">
                        {task.subtasks.filter(sub => sub.completed).length}/{task.subtasks.length} subtasks
                      </span>
                    </div>
                  )}
                </div>

                <div className="task-meta">
                  <div 
                    className="priority-badge"
                    style={{ backgroundColor: getPriorityColor(task.priority) }}
                    onClick={() => {
                      const priorities: Task['priority'][] = ['low', 'medium', 'high', 'urgent'];
                      const currentIndex = priorities.indexOf(task.priority);
                      const nextPriority = priorities[(currentIndex + 1) % priorities.length];
                      updateTaskPriority(task.id, nextPriority);
                    }}
                  >
                    {task.priority}
                  </div>

                  <div className="assignee-info">
                    {MOCK_USERS.find(u => u.name === task.assignee) && (
                      <>
                        <span className="assignee-avatar">
                          {MOCK_USERS.find(u => u.name === task.assignee)?.avatar}
                        </span>
                        <span className="assignee-name">{task.assignee}</span>
                      </>
                    )}
                  </div>

                  <div className="due-date">
                    📅 {new Date(task.dueDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="task-footer">
                <div className="time-tracking">
                  <span className="time-spent">⏱️ {Math.round(task.timeSpent / 60)}h logged</span>
                  <span className="time-estimated">📊 {Math.round(task.estimatedTime / 60)}h estimated</span>
                </div>
                
                <div className="task-actions">
                  <button className="action-btn edit" onClick={() => setEditingTask(task)}>✏️</button>
                  <button className="action-btn timer">▶️</button>
                  <button className="action-btn archive">📁</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="version-comparison-advanced">
        <Link to="?version=1.0" className="downgrade-link">
          Compare with Synoppy 1.0 Version →
        </Link>
      </div>

      <div className="footer-advanced">
        <div className="feature-highlights">
          <div className="feature">✅ Real-time collaboration</div>
          <div className="feature">🏷️ Advanced tagging & filtering</div>
          <div className="feature">⏱️ Time tracking & analytics</div>
          <div className="feature">👥 Team workspaces</div>
          <div className="feature">🔄 Drag & drop reordering</div>
          <div className="feature">📊 Progress tracking</div>
        </div>
      </div>
    </div>
  );
};

export default TodoAppAdvanced;